'use client';

import * as Sentry from '@sentry/browser';
import { QueryClientProvider } from '@tanstack/react-query';
import { nb, sv } from 'date-fns/locale';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import noLocale from 'i18n-iso-countries/langs/no.json';
import svLocale from 'i18n-iso-countries/langs/sv.json';
import { Suspense, useEffect, useRef } from 'react';
import { registerLocale } from 'react-datepicker';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ThemeProvider } from 'styled-components';

import queryClient from '@/legacy/data/QueryClient';
import AuthProvider from '@/legacy/providers/auth';
import GlobalStyle from '@/legacy/styled-components/layout/global-style';
import theme from '@/legacy/styled-components/layout/theme';

// Register date-fns locales
registerLocale('sv', sv);
registerLocale('nb', nb);

// Register i18n-iso-countries locales
countries.registerLocale(enLocale);
countries.registerLocale(svLocale);
countries.registerLocale(noLocale);

// Initialize i18n
import '@/legacy/i18n';

interface ClientProvidersProps {
  children: React.ReactNode;
}

/**
 * ClientProviders wraps the application with all necessary client-side providers:
 * - ThemeProvider (styled-components)
 * - GlobalStyle
 * - QueryClientProvider (TanStack Query)
 * - DndProvider (React DnD)
 *
 * Also handles initialization of:
 * - Sentry error tracking
 * - App configuration from env vars
 * - i18n locale registration
 */
export default function ClientProviders({ children }: ClientProvidersProps) {
  const sentryInitialized = useRef(false);

  useEffect(() => {
    if (sentryInitialized.current) return;
    sentryInitialized.current = true;

    // Initialize Sentry
    const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;
    const release = process.env.NEXT_PUBLIC_SENTRY_RELEASE;
    const environment = process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT;

    if (dsn) {
      Sentry.init({
        dsn,
        release,
        environment,
        integrations: [Sentry.browserTracingIntegration()],
        tracesSampleRate: 0,
        tunnel: '/api/s/e',
      });
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={null}>
          <AuthProvider>
            <DndProvider backend={HTML5Backend}>{children}</DndProvider>
          </AuthProvider>
        </Suspense>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
